// client/components/ontology-v1/ComplianceBar.tsx
// Pillar compliance bar with color coding
// Implementation Plan v1.2 §6

import React from 'react';

export interface PillarCompliance {
  pillar: "F" | "A" | "I" | "R";
  percent: number;  // 0-100
  passed: number;   // Rules passed
  total: number;    // Total rules for pillar
}

interface ComplianceBarProps {
  compliance: PillarCompliance;
}

const PILLAR_NAMES: Record<string, string> = {
  F: "Findable",
  A: "Accessible",
  I: "Interoperable",
  R: "Reusable",
};

/**
 * Get color based on percentage threshold
 * Green: ≥75%, Orange: 50-74%, Red: <50%
 */
function getColor(percent: number): string {
  if (percent >= 75) return "#22c55e"; // green-500
  if (percent >= 50) return "#f97316"; // orange-500
  return "#ef4444"; // red-500
}

export function ComplianceBar({ compliance }: ComplianceBarProps) {
  const { pillar, passed, total } = compliance;
  const raw = Number.isFinite(compliance.percent) ? compliance.percent : 0;
  const percent = Math.max(0, Math.min(100, Math.round(raw)));
  const color = getColor(percent);
  const pillarName = PILLAR_NAMES[pillar] || pillar;

  return (
    <div className="compliance-bar" style={{ marginBottom: "16px" }}>
      <div className="compliance-header" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            className="pillar-letter"
            style={{
              fontWeight: "bold",
              fontSize: "20px",
              color: color
            }}
          >
            {pillar}
          </span>
          <span
            className="pillar-name"
            style={{
              fontSize: "16px",
              color: "#374151" // gray-700
            }}
          >
            {pillarName}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            className="pillar-percent"
            style={{
              fontWeight: "600",
              fontSize: "18px",
              color: color
            }}
          >
            {percent}%
          </span>
          <span
            className="pillar-count"
            style={{
              fontSize: "14px",
              color: "#6b7280" // gray-500
            }}
          >
            {passed}/{total} rules
          </span>
        </div>
      </div>

      <div
        className="progress-bar-container"
        style={{
          width: "100%",
          height: "8px",
          backgroundColor: "#e5e7eb", // gray-200
          borderRadius: "9999px",
          overflow: "hidden"
        }}
      >
        <div
          className="progress-bar-fill"
          style={{
            height: "100%",
            width: `${percent}%`,
            backgroundColor: color,
            transition: "width 0.3s ease-in-out",
            borderRadius: "9999px"
          }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pillarName}: ${percent}% (${passed} of ${total} rules passed)`}
        />
      </div>
    </div>
  );
}
